
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import JobCard from '../components/JobCard';
import ResumeUploader from '../components/ResumeUploader';
import AIResumeAnalysis from '../components/AIResumeAnalysis';
import { ArrowRight, ArrowLeft, Lock } from 'lucide-react';

// Sample job listings
const jobs = [
  {
    id: '1',
    title: 'Senior Frontend Developer',
    department: 'Engineering',
    location: 'San Francisco, CA',
    remote: true,
    type: 'Full-time',
    salary: '₹120K - ₹150K',
    posted: '2 days ago',
    description: 'We\'re looking for an experienced frontend developer to join our team. You\'ll be working on our web application, building new features and improving existing ones.',
  },
  {
    id: '2',
    title: 'Product Manager',
    department: 'Product',
    location: 'New York, NY',
    remote: true,
    type: 'Full-time',
    salary: '₹130K - ₹160K',
    posted: '1 week ago',
    description: 'As a Product Manager, you\'ll work closely with engineering, design, and business teams to define product strategy and roadmap.',
  },
  {
    id: '3',
    title: 'UX/UI Designer',
    department: 'Design',
    location: 'Seattle, WA',
    remote: false,
    type: 'Full-time',
    salary: '₹110K - ₹140K',
    posted: '3 days ago',
    description: 'We are looking for a creative and passionate UX/UI Designer to join our growing team. You will be responsible for creating beautiful, intuitive interfaces.',
  },
  {
    id: '4',
    title: 'DevOps Engineer',
    department: 'Engineering',
    location: 'Austin, TX',
    remote: true,
    type: 'Full-time',
    salary: '₹125K - ₹155K',
    posted: '5 days ago',
    description: 'As a DevOps Engineer, you will be responsible for maintaining and improving our cloud infrastructure, CI/CD pipelines, and developer experience.',
  },
];

const JobApplication = () => {
  const navigate = useNavigate();
  const [selectedJobId, setSelectedJobId] = useState('');
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [currentCtc, setCurrentCtc] = useState('');
  const [expectedCtc, setExpectedCtc] = useState('');
  const [experience, setExperience] = useState('');
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Check if user is authenticated
    const authToken = localStorage.getItem('authToken');
    const userType = localStorage.getItem('userType');
    
    if (!authToken || userType !== 'candidate') {
      toast.error('Please log in as a candidate to continue');
      navigate('/login');
      return;
    }
    
    // Check if user came from signup
    const candidateInfo = sessionStorage.getItem('candidateInfo');
    if (!candidateInfo) {
      toast.error('You need to provide your information before applying for jobs');
      setIsAuthorized(false);
      
      // Redirect after showing error
      setTimeout(() => {
        navigate('/signup');
      }, 2000);
      return;
    }
    
    setIsAuthorized(true);
  }, [navigate]);

  useEffect(() => {
    // Check if form is complete
    setIsFormComplete(
      selectedJobId !== '' &&
      resumeFile !== null &&
      currentCtc !== '' &&
      expectedCtc !== '' &&
      experience !== ''
    );
  }, [selectedJobId, resumeFile, currentCtc, expectedCtc, experience]);

  const handleJobSelect = (id: string) => {
    setSelectedJobId(id);
  };

  const handleResumeUpload = (file: File) => {
    setResumeFile(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormComplete) {
      toast.error('Please complete all required fields');
      return;
    }

    // Apply rate limiting to prevent spamming
    const lastSubmitTime = localStorage.getItem('lastApplicationSubmit');
    const currentTime = Date.now();
    
    if (lastSubmitTime && (currentTime - parseInt(lastSubmitTime)) < 10000) {
      toast.error('Please wait before submitting another application');
      return;
    }
    
    localStorage.setItem('lastApplicationSubmit', currentTime.toString());

    // Save application data
    const applicationData = {
      jobId: selectedJobId,
      currentCtc,
      expectedCtc,
      experience
    };
    sessionStorage.setItem('applicationData', JSON.stringify(applicationData));

    // Navigate to assessment
    navigate('/assessment');
  };

  const handleBack = () => {
    navigate('/signup');
  };

  if (isAuthorized === false) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow pt-28 pb-16 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-4">
              <Lock className="h-8 w-8 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-apple-text mb-4">
              Access Restricted
            </h1>
            <p className="text-apple-text-secondary mb-6">
              You need to complete your profile before you can apply for jobs.
              Redirecting you to the profile page...
            </p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-apple-blue mx-auto"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  if (isAuthorized === null) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow pt-28 pb-16 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apple-blue"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow pt-28 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Progress indicator */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-apple-text-secondary">
                  Step 2 of 4
                </span>
                <span className="text-sm font-medium text-apple-blue">
                  Job Selection & Resume
                </span>
              </div>
              <div className="bg-gray-200 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-apple-blue h-full transition-all duration-500 ease-apple"
                  style={{ width: '50%' }}
                ></div>
              </div>
            </div>

            {/* Form card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 md:p-8">
                <h1 className="text-2xl md:text-3xl font-semibold text-apple-text mb-2">
                  Select Job & Upload Resume
                </h1>
                <p className="text-apple-text-secondary mb-8">
                  Choose a position you'd like to apply for and upload your resume. Our AI will analyze your experience.
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="mb-8">
                    <h2 className="text-xl font-medium text-apple-text mb-4">
                      1. Select a position
                    </h2>
                    <div className="grid grid-cols-1 gap-4">
                      {jobs.map((job) => (
                        <JobCard
                          key={job.id}
                          job={job}
                          isSelected={selectedJobId === job.id}
                          onSelect={handleJobSelect}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="mb-8">
                    <h2 className="text-xl font-medium text-apple-text mb-4">
                      2. Upload your resume
                    </h2>
                    <ResumeUploader onFileUpload={handleResumeUpload} />
                    <AIResumeAnalysis file={resumeFile} />
                  </div>

                  <div className="mb-8">
                    <h2 className="text-xl font-medium text-apple-text mb-6">
                      3. Additional information
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="currentCtc" className="block text-sm font-medium text-apple-text mb-1">
                          Current CTC (Annual)*
                        </label>
                        <input
                          type="text"
                          id="currentCtc"
                          value={currentCtc}
                          onChange={(e) => setCurrentCtc(e.target.value)}
                          className="apple-input"
                          placeholder="e.g. ₹80,000"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="expectedCtc" className="block text-sm font-medium text-apple-text mb-1">
                          Expected CTC (Annual)*
                        </label>
                        <input
                          type="text"
                          id="expectedCtc"
                          value={expectedCtc}
                          onChange={(e) => setExpectedCtc(e.target.value)}
                          className="apple-input"
                          placeholder="e.g. ₹100,000"
                          required
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label htmlFor="experience" className="block text-sm font-medium text-apple-text mb-1">
                          Years of Experience*
                        </label>
                        <input
                          type="number"
                          id="experience"
                          value={experience}
                          onChange={(e) => setExperience(e.target.value)}
                          className="apple-input"
                          placeholder="e.g. 5"
                          min="0"
                          max="50"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-between gap-4">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="order-2 sm:order-1 apple-btn-secondary flex items-center justify-center"
                    >
                      <ArrowLeft className="mr-2" size={18} />
                      <span>Back</span>
                    </button>
                    <button
                      type="submit"
                      disabled={!isFormComplete}
                      className={`order-1 sm:order-2 apple-btn flex items-center justify-center ${
                        !isFormComplete ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <span>Continue</span>
                      <ArrowRight className="ml-2" size={18} />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default JobApplication;
